import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HealthPackagesModal = ({ isOpen, onClose, initialData }) => {
  const [packagesData, setPackagesData] = useState({
    heading: "",
    subheading: "",
    packages: [],
    buttonText: "",
    buttonLink: "",

  });

  const [activeTab, setActiveTab] = useState('main');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(null);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/home/health-packages/`);
          if (response.ok) {
            const data = await response.json();
            setPackagesData({
              heading: data.heading || "",
              subheading: data.subheading || "",
              packages: data.packages || [],
              buttonText: data.buttonText || "",
              buttonLink:data.buttonLink || "",
            });
          }
        } catch (error) {
          console.error('Error fetching health packages data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setPackagesData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPackagesData(prev => ({ ...prev, [name]: value }));
  };

  const handlePackageChange = (index, field, value) => {
    const updatedPackages = [...packagesData.packages];
    updatedPackages[index] = { ...updatedPackages[index], [field]: value };
    setPackagesData(prev => ({ ...prev, packages: updatedPackages }));
  };

  const handleFeatureChange = (pkgIndex, featureIndex, value) => {
    const updatedPackages = [...packagesData.packages];
    const updatedFeatures = [...updatedPackages[pkgIndex].features];
    updatedFeatures[featureIndex] = value;
    updatedPackages[pkgIndex] = {
      ...updatedPackages[pkgIndex],
      features: updatedFeatures
    };
    setPackagesData(prev => ({ ...prev, packages: updatedPackages }));
  };

  const addPackage = () => {
    setPackagesData(prev => ({
      ...prev,
      packages: [...prev.packages, { 
        title: "",
        description: "",
        icon: "",
        features: [""],
        buttonText: "",
        buttonLink: ""
      }]
    }));
  };

  const removePackage = (index) => {
    const updatedPackages = [...packagesData.packages];
    updatedPackages.splice(index, 1);
    setPackagesData(prev => ({ ...prev, packages: updatedPackages }));
  };

  const addFeature = (pkgIndex) => {
    const updatedPackages = [...packagesData.packages];
    updatedPackages[pkgIndex].features.push("");
    setPackagesData(prev => ({ ...prev, packages: updatedPackages }));
  };

  const removeFeature = (pkgIndex, featureIndex) => {
    const updatedPackages = [...packagesData.packages];
    updatedPackages[pkgIndex].features.splice(featureIndex, 1);
    setPackagesData(prev => ({ ...prev, packages: updatedPackages }));
  };

  const handleIconUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'package_icons');

    try {
      setIsUploading(true);
      setCurrentUploadIndex(index);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      // Handle both response formats (direct URL or object with url property)
      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        handlePackageChange(index, 'icon', imageUrl);
      } else {
        throw new Error('Invalid image URL in response');
      }
    } catch (error) {
      console.error('Error uploading icon:', error);
      alert('Failed to upload icon. Please try again.');
    } finally {
      setIsUploading(false);
      setCurrentUploadIndex(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/home/health-packages/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(packagesData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update health packages data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating health packages data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading health packages data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Health Packages Section</h3>
        
        <div className="tabs tabs-boxed mb-6">
          <button 
            className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('main')}
          >
            Main Content
          </button>
          <button 
            className={`tab ${activeTab === 'packages' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('packages')}
          >
            Packages ({packagesData.packages.length})
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === 'main' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Heading</span>
                </label>
                <input 
                  type="text" 
                  name="heading" 
                  value={packagesData.heading || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Subheading</span>
                </label>
                <textarea 
                  name="subheading" 
                  value={packagesData.subheading || ''} 
                  onChange={handleChange} 
                  className="textarea textarea-bordered w-full h-32" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Text</span>
                </label>
                <input 
                  type="text" 
                  name="buttonText" 
                  value={packagesData.buttonText || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Link</span>
                </label>
                <input 
                  type="text" 
                  name="buttonLink" 
                  value={packagesData.buttonLink || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
            </div>
          )}
          
          {activeTab === 'packages' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold">Health Packages</h4>
                <button 
                  onClick={addPackage}
                  className="btn btn-sm btn-primary"
                >
                  Add Package
                </button>
              </div>

              {packagesData.packages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No packages added yet. Click "Add Package" to create one.
                </div>
              ) : (
                packagesData.packages.map((pkg, pkgIndex) => (
                  <div key={pkgIndex} className="card bg-base-100 shadow-sm p-4 relative mb-6">
                    <button 
                      onClick={() => removePackage(pkgIndex)}
                      className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                    >
                      ✕
                    </button>
                    
                    <h4 className="font-bold mb-3">Package {pkgIndex + 1}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Title</span>
                        </label>
                        <input 
                          type="text" 
                          value={pkg?.title || ''} 
                          onChange={(e) => handlePackageChange(pkgIndex, 'title', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Button Text</span>
                        </label>
                        <input 
                          type="text" 
                          value={pkg?.buttonText || ''} 
                          onChange={(e) => handlePackageChange(pkgIndex, 'buttonText', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Button Link</span>
                        </label>
                        <input 
                          type="text" 
                          value={pkg?.buttonLink || ''} 
                          onChange={(e) => handlePackageChange(pkgIndex, 'buttonLink', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Icon Image</span>
                        </label>
                        <div className="flex items-center gap-2">
                          {pkg?.icon && (
                            <div className="avatar">
                              <div className="w-8 rounded">
                                <img src={pkg.icon} alt="Package icon" />
                              </div>
                            </div>
                          )}
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => handleIconUpload(e, pkgIndex)}
                            className="file-input file-input-bordered file-input-sm w-full max-w-xs"
                            disabled={isUploading && currentUploadIndex === pkgIndex}
                          />
                          {isUploading && currentUploadIndex === pkgIndex && (
                            <progress 
                              className="progress progress-primary w-20" 
                              value={uploadProgress} 
                              max="100"
                            ></progress>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Description</span>
                      </label>
                      <textarea 
                        value={pkg?.description || ''} 
                        onChange={(e) => handlePackageChange(pkgIndex, 'description', e.target.value)} 
                        className="textarea textarea-bordered w-full h-24" 
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Features</span>
                        <button 
                          onClick={() => addFeature(pkgIndex)}
                          className="btn btn-xs btn-primary"
                        >
                          Add Feature
                        </button>
                      </label>
                      
                      {pkg?.features?.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-2 mb-2">
                          <input 
                            type="text" 
                            value={feature || ''} 
                            onChange={(e) => handleFeatureChange(pkgIndex, featureIndex, e.target.value)} 
                            className="input input-bordered w-full" 
                          />
                          <button 
                            onClick={() => removeFeature(pkgIndex, featureIndex)}
                            className="btn btn-circle btn-xs btn-error"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default HealthPackagesModal;